import {
    Card,
    CardBody,
    Typography,
    Button,
    Input,
    Dialog,
    IconButton,
    DialogBody,
    DialogHeader,
    DialogFooter,

} from "@material-tailwind/react";
import {
    ArrowLongUpIcon,
    ArrowLongDownIcon,
    CheckCircleIcon,
    PencilSquareIcon,
    PlusCircleIcon,
    MagnifyingGlassIcon,
    TrashIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import React, {useEffect, useMemo, useRef, useState} from "react";
import getData from "../../api/axiosInstance";
import axios from "axios";
import {useTheme} from "../common/ThemeContext";
import {toLocalDateTimeInputValue} from "../../utils/dateTime";

export default function OperatingExpensesDashboard() {

    // state management
    const [expensesList, setExpensesList] = useState([]);
    const [sortSettings, setSortSettings] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
    const [expenseToEdit, setExpenseToEdit] = useState({});
    const [deleteTimer, setDeleteTimer] = useState(null);
    const [newExpenseData, setNewExpenseData] = useState({
        id: '',
        description: '',
        price: '',
        date: toLocalDateTimeInputValue(),
    });
    const { isDark } = useTheme();

    // refs and constants
    const timerRef = useRef(null);

    const token = localStorage.getItem("token");
    const requestHeaders = useMemo(() => {
        return {
            'Authorization': `Bearer ${token}`
        };
    }, [token]);


    // data transformations
    const filteredExpenses = expensesList.filter(expense => {
        const queryWords = searchTerm.toLowerCase().split(' ').filter(Boolean);
        return queryWords.every(word => expense.description.toLowerCase().includes(word));
    });

    const sortedExpenses = sortSettings
        ? [...filteredExpenses]
            .sort((a, b) => {
                const aValue = a[sortSettings.key];
                const bValue = b[sortSettings.key];

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    const parsedA = parseDescriptionForSorting(aValue);
                    const parsedB = parseDescriptionForSorting(bValue);
                    for (let i = 0; i < Math.max(parsedA.length, parsedB.length); i++) {
                        if (parsedA[i] < parsedB[i]) return sortSettings.direction === 'ascending' ? -1 : 1;
                        if (parsedA[i] > parsedB[i]) return sortSettings.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                }

                if (aValue < bValue) return sortSettings.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortSettings.direction === 'ascending' ? 1 : -1;
                return 0;
            })
        : filteredExpenses;

    // utility functions
    const parseDescriptionForSorting = (description) => {
        return description
            .toLowerCase()
            .split(/(\d+)/)
            .map(part => isNaN(part) ? part : parseInt(part, 10));
    };

    const resetExpenseForm = () => {
        setNewExpenseData({
            id: '',
            description: '',
            price: '',
            date: toLocalDateTimeInputValue(),
        });
    };

    // event handlers
    const toggleAddDialog = () => setIsAddDialogOpen(!isAddDialogOpen);
    const toggleEditDialog = (expense) => {
        setExpenseToEdit(expense);
        setIsEditDialogOpen(!isEditDialogOpen);
    };

    const handleNewExpenseInput = (e) => {
        const { name, value } = e.target;
        setNewExpenseData(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleEditExpenseInput = (e) => {
        const { name, value } = e.target;
        setExpenseToEdit((prevData) => {
            const updatedData = {
                ...prevData,
                [name]: value,
            };
            console.log("Updated operating expense:", updatedData);
            return updatedData;
        });
    };

    const handleColumnSort = (key) => {
        let direction = 'ascending';
        if (sortSettings && sortSettings.key === key && sortSettings.direction === 'ascending') {
            direction = 'descending';
        }
        setSortSettings({ key, direction });
    };

    const handleAddExpense = async (event) => {
        event.preventDefault();
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

        try {
            await axios.post(
                `${API_URL}/operating_expense/add`,
                newExpenseData,
                { headers: requestHeaders }
            );

            // Nakon uspešnog dodavanja, dohvati sve podatke o operating expenses
            const response = await axios.get(
                `${API_URL}/operating_expense/all`,
                { headers: requestHeaders }
            );

            setExpensesList(response.data);
            setIsAddDialogOpen(false);
            resetExpenseForm();

        } catch (err) {
            console.error("Error adding expense:", err);
        }
    };

    const handleUpdateExpense = async (event) => {
        event.preventDefault();
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

        try {
            await axios.put(
                `${API_URL}/operating_expense/edit/${expenseToEdit.id}`,
                expenseToEdit,
                { headers: requestHeaders }
            );

            // Nakon uspešnog dodavanja, dohvati sve podatke o operating expenses
            const response = await axios.get(
                `${API_URL}/operating_expense/all`,
                { headers: requestHeaders }
            );

            setExpensesList(response.data);
            setIsEditDialogOpen(false);

        } catch (err) {
            console.error("Error updating expense:", err);
        }
    };

    const handleDeleteExpense = async () => {
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

        if (!expenseToEdit?.id) return;

        try {
            await axios.delete(
                `${API_URL}/operating_expense/delete/${expenseToEdit.id}`,
                { headers: requestHeaders }
            );

            // Nakon uspešnog dodavanja, dohvati sve podatke o operating expenses
            const response = await axios.get(
                `${API_URL}/operating_expense/all`,
                { headers: requestHeaders }
            );

            setExpensesList(response.data);
            setIsEditDialogOpen(false);
            setDeleteTimer(null);

        } catch (err) {
            console.error("Error deleting expense:", err);
        }
    };

    const startDeleteCountdown = () => {
        if (deleteTimer !== null) {
            clearInterval(timerRef.current);
            setDeleteTimer(null);
            return;
        }

        let timeLeft = 5;
        setDeleteTimer(timeLeft);

        timerRef.current = setInterval(() => {
            timeLeft -= 1;
            setDeleteTimer(timeLeft);

            if (timeLeft === 0) {
                clearInterval(timerRef.current);
                handleDeleteExpense();
            }
        }, 1000);
    };

    // side effects
    useEffect(() => {
        getData(
            "/operating_expense/all",
            requestHeaders,
            setExpensesList,
            "Error fetching expenses data"
        )
    }, [requestHeaders]);

    useEffect(() => {
        if (isEditDialogOpen && expenseToEdit?.id) {
            const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

            axios
                .get(
                    `${API_URL}/operating_expense/${expenseToEdit.id}`,
                    { headers: requestHeaders }
                )
                .then(response => setExpenseToEdit(response.data))
                .catch(error => console.error("Error fetching expense data:", error));
        }
    }, [isEditDialogOpen, requestHeaders, expenseToEdit?.id]);


    return (
        <Card className="w-full shadow-[0_0_15px_rgba(0,0,0,0.1)] relative overflow-hidden dark:bg-dark-900">
            <CardBody className="relative">
                <Typography variant="h5" color="blue-gray" className="mb-4 flex items-center justify-between dark:text-gray-300">
                    Operating Expenses
                    <Button onClick={toggleAddDialog} size="md" className="mv-primary-button">
                        <PlusCircleIcon className="h-4 w-4" />
                        Add
                    </Button>
                </Typography>
                <hr className="my-2 border-blue-gray-50 dark:border-gray-700" />

                <div className="relative overflow-x-auto h-[calc(100vh-24rem)] scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-dark-800">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="sticky top-0 bg-gray-50 z-10 dark:bg-dark-800">
                        <tr>
                            <th className="w-[15px]">
                                <span className="invisible">.</span>
                            </th>

                                <th onClick={() => handleColumnSort('id')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 dark:bg-dark-800 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[85px]">
                                ID
                                {sortSettings && sortSettings.key === 'id' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('description')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 dark:bg-dark-800 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[200px]">
                                Description
                                {sortSettings && sortSettings.key === 'description' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('price')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 dark:bg-dark-800 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[135px]">
                                Price
                                {sortSettings && sortSettings.key === 'price' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                            <th onClick={() => handleColumnSort('date')} className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 dark:bg-dark-800 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[150px]">
                                Date
                                {sortSettings && sortSettings.key === 'date' && (
                                    sortSettings.direction === 'ascending'
                                        ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                        : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                )}
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y dark:bg-dark-900 divide-gray-300 dark:divide-gray-700">
                        {sortedExpenses.map((operatingExpense) => (
                            <tr key={operatingExpense.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                    <PencilSquareIcon
                                        onClick={() => toggleEditDialog(operatingExpense)}
                                        className="w-4 h-4 text-cyan-900 dark:text-cyan-200 cursor-pointer mr-2"
                                    />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                    {operatingExpense.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                    {operatingExpense.description}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <p className="text-sm text-gray-900 dark:text-gray-300 font-bold">
                                        {Number.isFinite(operatingExpense.price)
                                            ? operatingExpense.price.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                            : '/'}
                                    </p>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {operatingExpense.date ?
                                        new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(operatingExpense.date))
                                        : '/'
                                    }
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center mt-5 w-full gap-2 md:w-max">
                    <div className="relative">
                        <Input
                            type="search"
                            placeholder="Search"
                            containerProps={{
                                className: "min-w-full sm:min-w-[288px] dark:border-gray-600 dark:focus-within:border-gray-500",
                            }}
                            className="!border-t-blue-gray-300 pl-9 placeholder:text-blue-gray-300 focus:!border-blue-gray-300 dark:bg-dark-800 dark:text-white dark:placeholder:text-gray-400 dark:border-gray-600 dark:focus:border-gray-500"
                            labelProps={{
                                className: "before:content-none after:content-none",
                            }}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            color={isDark ? "white" : "gray"}
                        />
                        <div className="!absolute left-3 top-[13px]">
                            <svg
                                width="13"
                                height="14"
                                viewBox="0 0 14 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="dark:[&_path]:fill-gray-400 dark:[&_path]:stroke-gray-400"
                            >
                                <path
                                    d="M9.97811 7.95252C10.2126 7.38634 10.3333 6.7795 10.3333 6.16667C10.3333 4.92899 9.84167 3.742 8.9665 2.86683C8.09133 1.99167 6.90434 1.5 5.66667 1.5C4.42899 1.5 3.242 1.99167 2.36683 2.86683C1.49167 3.742 1 4.92899 1 6.16667C1 6.7795 1.12071 7.38634 1.35523 7.95252C1.58975 8.51871 1.93349 9.03316 2.36683 9.4665C2.80018 9.89984 3.31462 10.2436 3.88081 10.4781C4.447 10.7126 5.05383 10.8333 5.66667 10.8333C6.2795 10.8333 6.88634 10.7126 7.45252 10.4781C8.01871 10.2436 8.53316 9.89984 8.9665 9.4665C9.39984 9.03316 9.74358 8.51871 9.97811 7.95252Z"
                                    fill="#CFD8DC"
                                />
                                <path
                                    d="M13 13.5L9 9.5M10.3333 6.16667C10.3333 6.7795 10.2126 7.38634 9.97811 7.95252C9.74358 8.51871 9.39984 9.03316 8.9665 9.4665C8.53316 9.89984 8.01871 10.2436 7.45252 10.4781C6.88634 10.7126 6.2795 10.8333 5.66667 10.8333C5.05383 10.8333 4.447 10.7126 3.88081 10.4781C3.31462 10.2436 2.80018 9.89984 2.36683 9.4665C1.93349 9.03316 1.58975 8.51871 1.35523 7.95252C1.12071 7.38634 1 6.7795 1 6.16667C1 4.92899 1.49167 3.742 2.36683 2.86683C3.242 1.99167 4.42899 1.5 5.66667 1.5C6.90434 1.5 8.09133 1.99167 8.9665 2.86683C9.84167 3.742 10.3333 4.92899 10.3333 6.16667Z"
                                    stroke="#CFD8DC"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    </div>
                    <Button
                        size="md"
                        className="mv-primary-button"
                    >
                        <MagnifyingGlassIcon className="h-4 w-4" />
                        Search
                    </Button>
                </div>

                <Dialog size="sm" open={isAddDialogOpen} handler={toggleAddDialog} className="p-4 mt-10 mb-10">
                    <DialogHeader className="relative m-0 block">
                        <Typography variant="h4" color="blue-gray">
                            Add operating expense
                        </Typography>
                        <Typography className="mt-1 font-normal text-gray-600">
                            Import operating expense.
                        </Typography>
                        <IconButton
                            size="sm"
                            variant="text"
                            className="!absolute right-3.5 top-3.5"
                            onClick={toggleAddDialog}
                        >
                            <XMarkIcon className="h-4 w-4 stroke-2" />
                        </IconButton>
                    </DialogHeader>
                    <form onSubmit={handleAddExpense}>
                        <DialogBody className="space-y-4 pb-6 h-[16rem] overflow-scroll">
                            <div>
                                <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                    Description
                                </Typography>
                                <Input
                                    color="gray"
                                    size="lg"
                                    placeholder="eg. Marketing"
                                    name="description"
                                    value={newExpenseData.description}
                                    onChange={handleNewExpenseInput}
                                    className="placeholder:opacity-100 focus:!border-t-gray-900"
                                    containerProps={{
                                        className: "!min-w-full",
                                    }}
                                    labelProps={{
                                        className: "hidden",
                                    }}
                                />
                            </div>

                            <div className="flex gap-4">
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Price (RSD)
                                    </Typography>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. 100.00"
                                        name="price"
                                        value={newExpenseData.price}
                                        onChange={handleNewExpenseInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Date
                                    </Typography>
                                    <Input
                                        type="datetime-local"
                                        color="gray"
                                        size="lg"
                                        placeholder="Select date and time"
                                        name="date"
                                        value={newExpenseData.date}
                                        onChange={handleNewExpenseInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                            </div>
                        </DialogBody>
                        <DialogFooter>
                            <Button type="submit" className="mv-primary-button ml-auto">
                                <PlusCircleIcon className="h-4 w-4" />
                                Add Operating Expense
                            </Button>
                        </DialogFooter>
                    </form>
                </Dialog>

                <Dialog size="sm" open={isEditDialogOpen} handler={toggleEditDialog} className="p-4 mt-10 mb-10">
                    <DialogHeader className="relative m-0 block">
                        <Typography variant="h4" color="blue-gray">
                            Edit operating expense
                        </Typography>
                        <Typography className="mt-1 font-normal text-gray-600">
                            Update operating expense.
                        </Typography>
                        <IconButton
                            size="sm"
                            variant="text"
                            className="!absolute right-3.5 top-3.5"
                            onClick={toggleEditDialog}
                        >
                            <XMarkIcon className="h-4 w-4 stroke-2" />
                        </IconButton>
                    </DialogHeader>
                    <form onSubmit={handleUpdateExpense}>
                        <DialogBody className="space-y-4 pb-6 h-[16rem] overflow-scroll">
                            <div>
                                <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                    Description
                                </Typography>
                                <Input
                                    color="gray"
                                    size="lg"
                                    placeholder="eg. Marketing"
                                    name="description"
                                    value={expenseToEdit?.description || ''}
                                    onChange={handleEditExpenseInput}
                                    className="placeholder:opacity-100 focus:!border-t-gray-900"
                                    containerProps={{
                                        className: "!min-w-full",
                                    }}
                                    labelProps={{
                                        className: "hidden",
                                    }}
                                />
                            </div>

                            <div className="flex gap-4">
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Price (RSD)
                                    </Typography>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        color="gray"
                                        size="lg"
                                        placeholder="eg. 100.00"
                                        name="price"
                                        value={expenseToEdit.price}
                                        onChange={handleEditExpenseInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                                <div className="w-full">
                                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                        Date
                                    </Typography>
                                    <Input
                                        type="datetime-local"
                                        color="gray"
                                        size="lg"
                                        placeholder="Select date and time"
                                        name="date"
                                        value={expenseToEdit.date}
                                        onChange={handleEditExpenseInput}
                                        className="placeholder:opacity-100 focus:!border-t-gray-900"
                                        containerProps={{
                                            className: "!min-w-full",
                                        }}
                                        labelProps={{
                                            className: "hidden",
                                        }}
                                    />
                                </div>
                            </div>
                        </DialogBody>
                        <DialogFooter className="flex items-center justify-between gap-2">
                            <Button
                                onClick={startDeleteCountdown}
                                className={deleteTimer !== null ? "mv-warning-button" : "mv-danger-button"}
                            >
                                <TrashIcon className="h-4 w-4" />
                                {deleteTimer !== null ? `Abandon (${deleteTimer})` : "Delete"}
                            </Button>
                            <Button type="submit" className="mv-primary-button">
                                <CheckCircleIcon className="h-4 w-4" />
                                Save
                            </Button>
                        </DialogFooter>
                    </form>
                </Dialog>
            </CardBody>
        </Card>
    );
}
